/**
 * §6.4 — IMPORTANCE INDEX.
 *
 * *"Users band and revenue band (1-5 each); default relative banding (quintiles across the
 * initiative's assessment units), absolute thresholds optional. Importance Index = 0.5 × users
 * band + 0.5 × revenue band."*
 *
 * D-004: both bands 1-100%, the index 1-100%, and *"Importance stays 0.5 × users + 0.5 ×
 * revenue"*.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════════════╗
 * ║ ⚠ THIS IS THE ONE UNDER-SPECIFIED RULE IN §6, AND IT NEEDS THE OWNER.                  ║
 * ║                                                                                        ║
 * ║ *"Quintiles across the initiative's assessment units"* does not say:                    ║
 * ║                                                                                        ║
 * ║  1. where a boundary falls when the unit count is NOT a multiple of five — 7 units      ║
 * ║     cannot be split into five equal parts, so some band holds 2 and some hold 1, and    ║
 * ║     WHICH is a choice;                                                                  ║
 * ║  2. how TIES break — two units with the same user count can land in different bands     ║
 * ║     under a rank-based rule, which means the band depends on the order rows came back    ║
 * ║     from Postgres unless something pins it;                                             ║
 * ║  3. whether a unit with a NULL users or revenue figure is banded at all.                 ║
 * ║                                                                                        ║
 * ║ A8 implemented one reading in `test/erp-consolidation/scoring-spec.ts` and flagged it    ║
 * ║ rather than pretending it was settled. **This file implements the SAME reading**, so     ║
 * ║ the specification and the engine agree and the cross-check is meaningful — but agreeing  ║
 * ║ with each other is not the same as being right, and the owner has not ruled.             ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════╝
 *
 * THE READING, stated so it can be overruled in one place:
 *
 *   - Sort ascending by value, **ties broken by the unit's own id** so the result is stable
 *     across runs and across machines. This answers (2) by making the band deterministic, but
 *     it does NOT make equal values share a band — two units on 500 users can land in
 *     different quintiles, which is the part most likely to surprise a client.
 *   - `quintile = ceil(rank × 5 / n)` on the 1-based ascending rank. This answers (1), but
 *     **not tidily, and the consequences were measured rather than assumed**:
 *
 *       n = 7  → bands 1,2,3,3,4,5,5   (the extra members land in bands 3 and 5, not in the
 *                                       low bands — `ceil` puts them wherever the division
 *                                       falls, which is not a property anyone would choose)
 *       n = 13 → bands 1,1,2,2,2,3,3,4,4,4,5,5,5
 *       n = 2  → bands 3,5             ← **the bottom band is UNREACHABLE below 5 units**
 *       n = 1  → band 5                ← a lone unit is its own top quintile
 *
 *     That last row is the part most likely to be called a bug: an initiative with four
 *     assessment units can produce no "20%" importance at all, and a single unit scores
 *     100% importance on no evidence whatsoever. It follows from "quintiles" taken
 *     literally — five bands need five members — and it is a real reason the owner may
 *     prefer absolute thresholds for small initiatives.
 *   - A `null` figure is treated as the lowest possible value and still banded, because
 *     `assessment_unit.users_count` and `revenue_amount` are both NULLABLE and a unit that
 *     has not been filled in yet still has to render a row. It is NOT excluded, because
 *     excluding it would change every OTHER unit's band — `n` would differ — and a half-
 *     entered grid would then renumber itself as data arrived.
 *
 * ALTERNATIVES the owner may prefer, named so the question is answerable without re-deriving
 * it: equal-width bands over the observed range (no rank, ties always share a band, but one
 * outlier flattens everything else); percentile with ties sharing the higher band; or absolute
 * thresholds, which §6.4 already allows as *"optional"* and which `bandByAbsoluteThresholds`
 * below implements — though note `scoring_config` has **no columns for them** today, so
 * choosing that route needs a migration.
 */
import {
  assertFactorPair,
  assertPctTenths,
  ONE_HUNDRED_PCT_TENTHS,
  roundHalfUp,
} from './units';
import { DEFAULT_BAND_VALUES_TENTHS } from './complexity';

export interface ImportanceInput {
  readonly id: string;
  /** `null` is banded as the lowest value — see this file's header, point 3. */
  readonly usersCount: number | null;
  readonly revenueAmount: number | null;
}

export interface ImportanceFactors {
  /** §6.4's 0.5, in tenths: 500. */
  readonly usersWeightTenths: number;
  /** §6.4's other 0.5. The two must sum to 1000 — `0096` has the CHECK. */
  readonly revenueWeightTenths: number;
  readonly bandValuesTenths?: readonly number[];
}

/** §6.4's defaults, which are also `scoring_config`'s column defaults. */
export const DEFAULT_IMPORTANCE_FACTORS: ImportanceFactors = {
  usersWeightTenths: 500,
  revenueWeightTenths: 500,
};

/**
 * How the two bands are derived. §6.4 names both: quintiles by default, absolute *"optional"*.
 *
 * Passed in rather than chosen inside, as §2.2 requires of every band and threshold.
 */
export type ImportanceBanding =
  | { readonly kind: 'quintile' }
  | {
      readonly kind: 'absolute';
      /** Four ascending INCLUSIVE upper bounds; the fifth band is open-ended. */
      readonly usersBoundaries: readonly number[];
      readonly revenueBoundaries: readonly number[];
    };

export const DEFAULT_IMPORTANCE_BANDING: ImportanceBanding = { kind: 'quintile' };

export interface ImportanceResult {
  readonly id: string;
  readonly usersBandTenths: number;
  readonly revenueBandTenths: number;
  /** The Importance Index, in ten-thousandths of a percentage point. */
  readonly pctTenThousandths: number;
}

/**
 * §6.4's index for every unit in one initiative.
 *
 * Relative banding is a function of the WHOLE SET, which is why this takes an array and returns
 * an array: a single unit's importance is not computable on its own, and an API that pretended
 * otherwise would quietly band every unit as the top quintile (`n = 1`).
 *
 * The result array is in INPUT order, not sorted order, so a caller can zip it against the rows
 * it passed in. §6.5's `comparePriority` is what sorts.
 */
export function importanceIndex(
  units: readonly ImportanceInput[],
  factors: ImportanceFactors = DEFAULT_IMPORTANCE_FACTORS,
  banding: ImportanceBanding = DEFAULT_IMPORTANCE_BANDING,
): readonly ImportanceResult[] {
  assertFactorPair(factors.usersWeightTenths, factors.revenueWeightTenths, 'importance');
  const bandValues = factors.bandValuesTenths ?? DEFAULT_BAND_VALUES_TENTHS;
  for (const v of bandValues) assertPctTenths(v, 'importance band value');

  const userBands =
    banding.kind === 'quintile'
      ? quintileBandsTenths(
          units.map((u) => ({ id: u.id, value: u.usersCount ?? 0 })),
          bandValues,
        )
      : absoluteBandsTenths(
          units.map((u) => ({ id: u.id, value: u.usersCount ?? 0 })),
          banding.usersBoundaries,
          bandValues,
        );

  const revenueBands =
    banding.kind === 'quintile'
      ? quintileBandsTenths(
          units.map((u) => ({ id: u.id, value: u.revenueAmount ?? 0 })),
          bandValues,
        )
      : absoluteBandsTenths(
          units.map((u) => ({ id: u.id, value: u.revenueAmount ?? 0 })),
          banding.revenueBoundaries,
          bandValues,
        );

  return units.map((u) => {
    const ub = userBands.get(u.id) ?? bandValues[0]!;
    const rb = revenueBands.get(u.id) ?? bandValues[0]!;
    return {
      id: u.id,
      usersBandTenths: ub,
      revenueBandTenths: rb,
      pctTenThousandths: factors.usersWeightTenths * ub + factors.revenueWeightTenths * rb,
    };
  });
}

/**
 * THE QUINTILE READING. See this file's header — every line of this function is a choice §6.4
 * left open, and the owner has not ruled on any of them.
 *
 * An EMPTY set returns an empty map rather than throwing: an initiative with no assessment units
 * yet is the normal state before the grid is filled, and the screen that shows "no units" must
 * not be the screen that crashes.
 */
function quintileBandsTenths(
  values: readonly { id: string; value: number }[],
  bandValuesTenths: readonly number[],
): Map<string, number> {
  const n = values.length;
  const out = new Map<string, number>();
  if (n === 0) return out;
  const sorted = [...values].sort((a, b) => a.value - b.value || compareIds(a.id, b.id));
  sorted.forEach((row, i) => {
    const rank = i + 1; // 1-based, ascending
    const quintile = Math.min(5, Math.max(1, Math.ceil((rank * 5) / n)));
    out.set(row.id, bandValuesTenths[quintile - 1]!);
  });
  return out;
}

/**
 * §6.4's *"absolute thresholds optional"*.
 *
 * Four ascending inclusive upper bounds, exactly the form §6.3's band boundaries take, so the
 * module has one shape of band specification and not two. Unlike the quintile reading this has
 * no ambiguity at all — a value is in a band or it is not — which is why it is worth offering
 * even though **`scoring_config` carries no columns for it today**: choosing it is a migration,
 * not a rewrite.
 */
function absoluteBandsTenths(
  values: readonly { id: string; value: number }[],
  boundaries: readonly number[],
  bandValuesTenths: readonly number[],
): Map<string, number> {
  if (boundaries.length !== bandValuesTenths.length - 1) {
    throw new Error(
      `absolute banding: expected ${bandValuesTenths.length - 1} boundaries, got ${boundaries.length}`,
    );
  }
  const out = new Map<string, number>();
  for (const row of values) out.set(row.id, bandByAbsoluteThresholds(row.value, boundaries, bandValuesTenths));
  return out;
}

/** One value against ascending inclusive upper bounds. Exported so a screen can label a row. */
export function bandByAbsoluteThresholds(
  value: number,
  boundaries: readonly number[],
  bandValuesTenths: readonly number[] = DEFAULT_BAND_VALUES_TENTHS,
): number {
  for (let i = 0; i < boundaries.length; i++) {
    if (value <= boundaries[i]!) return bandValuesTenths[i]!;
  }
  return bandValuesTenths[boundaries.length]!;
}

/**
 * The share of the top band, as a percentage in tenths — the one summary a landscape screen
 * needs that cannot be read off a single row.
 *
 * Here rather than in a screen because it divides, and a division done in two places rounds in
 * two directions.
 */
export function topBandShareTenths(results: readonly ImportanceResult[]): number {
  if (results.length === 0) return 0;
  const top = DEFAULT_BAND_VALUES_TENTHS[DEFAULT_BAND_VALUES_TENTHS.length - 1]!;
  const count = results.filter(
    (r) => r.usersBandTenths === top || r.revenueBandTenths === top,
  ).length;
  return roundHalfUp(count * ONE_HUNDRED_PCT_TENTHS, results.length);
}

/**
 * Ties broken by id, and by CODE POINT rather than by locale.
 *
 * `localeCompare` is what A8's specification used, and for the ids this module generates —
 * `gen_random_uuid()::text`, so `[0-9a-f-]` — the two orders are identical, which is why the
 * cross-check passes either way. `<` is used here because a locale-sensitive comparison can
 * differ between a Node process and a browser, and these two sides must agree by construction
 * (D-009) rather than because their ICU data happens to match.
 */
function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
